'use client';

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useSWR from 'swr';
import * as Sentry from '@sentry/nextjs';
import { useMediaQuery } from '@custom-react-hooks/use-media-query';

import { reveal } from '../../../utils/reveal';
import { elementOf } from './anchoring';
import { commentsMarkedAt, paintMarks, scrollToRange } from './marks';
import usePlacements, { NO_PLACED_RANGES } from './usePlacements';

export const COMMENTS_URL = '/wt/report-comments/';

// The ML breakpoint, where the report page gets its right column.
const RIGHT_COLUMN_QUERY = '(min-width: 1080px)';

export const useHasRightColumn = () => useMediaQuery(RIGHT_COLUMN_QUERY);

const commentsUrl = (pageId) => `${COMMENTS_URL}?page_id=${pageId}`;

const fetchComments = async (url) => {
    const response = await fetch(url, {
        headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
        const error = new Error(`${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
    }
    return response.json();
};

// The API's errors on these are about the selection rather than the comment.
export const SELECTION_FIELDS = [
    'selected_text',
    'selected_text_display',
    'prefix',
    'suffix',
    'position',
];

const withoutSelectionErrors = (errors) => {
    const kept = Object.fromEntries(
        Object.entries(errors ?? {}).filter(
            ([field]) => !SELECTION_FIELDS.includes(field)
        )
    );
    return Object.keys(kept).length > 0 ? kept : null;
};

const NO_ACTIVE_COMMENTS = [];

const ReportCommentsContext = createContext({
    pageId: null,
    readerName: null,
    comments: null,
    canComment: false,
    commentableRef: { current: null },
    isPanelOpen: false,
    isPanelCollapsed: false,
    toggleCollapsed: () => {},
    togglePanel: () => {},
    closePanel: () => {},
    draft: null,
    startDraft: () => {},
    updateDraft: () => {},
    discardDraft: () => {},
    commentPosted: () => {},
    commentDeleted: () => {},
    activeCommentIds: NO_ACTIVE_COMMENTS,
    placedRanges: NO_PLACED_RANGES,
    draftRange: null,
    showComment: () => {},
    selectionChanged: () => {},
});

// Holds the report's comments for everything that shows them, so they share
// one request. Until they have loaded, or if loading fails, `comments` is
// null and nothing about comments is shown.
export const ReportCommentsProvider = ({ pageId, readerName, children }) => {
    const { data, mutate } = useSWR(
        pageId ? commentsUrl(pageId) : null,
        fetchComments,
        {
            onError: (error) => {
                // A preview of a report that isn't published yet always gets
                // a 404, and has no comments.
                if (error.status === 404) return;
                console.error('Failed to load report comments:', error);
                Sentry.captureException(error);
            },
            // Revalidation on focus tries again. Retrying on a timer as well
            // would report one outage over and over.
            shouldRetryOnError: false,
        }
    );
    // The report text readers can comment on.
    const commentableRef = useRef(null);
    const comments = data?.comments ?? null;
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    // Whether the bottom sheet shows only what the reader is on, so the report
    // has room above it. The right column always shows them all.
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    // The comment being written: the selection it is about, what the reader
    // has typed, and whether posting it failed.
    const [draft, setDraft] = useState(null);
    const placements = usePlacements(
        commentableRef,
        comments,
        draft?.selection ?? null
    );
    const placedRanges = placements.comments;
    const [activeCommentIds, setActiveCommentIds] =
        useState(NO_ACTIVE_COMMENTS);
    // Whether the reader has selected text they can comment on.
    const [hasSelection, selectionChanged] = useState(false);

    // A scroll to a mark waiting for its accordion to open. Showing another
    // comment or closing the panel calls it off.
    const pendingScroll = useRef(null);
    useEffect(() => () => clearTimeout(pendingScroll.current), []);

    // Closing throws away the draft without asking.
    const closePanel = useCallback(() => {
        clearTimeout(pendingScroll.current);
        setIsPanelOpen(false);
        setIsPanelCollapsed(false);
        setDraft(null);
        setActiveCommentIds(NO_ACTIVE_COMMENTS);
    }, []);

    const togglePanel = useCallback(() => {
        if (isPanelOpen) {
            closePanel();
        } else {
            setIsPanelOpen(true);
        }
    }, [isPanelOpen, closePanel]);

    const toggleCollapsed = useCallback(
        () => setIsPanelCollapsed((current) => !current),
        []
    );

    // A new selection moves an existing draft to it and keeps what the reader
    // typed, and what the API said about it.
    //
    // The draft is what the reader is on now, so the comment they were on
    // fades with the rest. Below 1080px that is also the comment shown in the
    // report, which stays active once the bottom sheet closes. Otherwise the
    // sheet would open by scrolling away from the draft to it.
    const startDraft = useCallback((selection) => {
        setDraft((current) => ({
            selection,
            comment: current?.comment ?? '',
            errors: withoutSelectionErrors(current?.errors),
            failed: false,
        }));
        setActiveCommentIds(NO_ACTIVE_COMMENTS);
        setIsPanelOpen(true);
        setIsPanelCollapsed(true);
    }, []);

    const updateDraft = useCallback(
        (changes) =>
            setDraft((current) => current && { ...current, ...changes }),
        []
    );

    const discardDraft = useCallback(() => setDraft(null), []);

    // Makes a placed comment the active one and scrolls to its mark with
    // `scrollTo`. It stays active until another comment is, or the panel
    // closes. The bottom sheet passes a scroll that keeps the mark above it,
    // and shrinks to the comment.
    //
    // A mark in a collapsed accordion is scrolled to once the accordion has
    // opened. Until then the report is shorter, and a scroll to a mark near
    // its end would fall short.
    const showComment = useCallback(
        (id, scrollTo = scrollToRange) => {
            const range = placedRanges.get(id);
            if (!range) return;
            setActiveCommentIds([id]);
            setIsPanelCollapsed(true);
            clearTimeout(pendingScroll.current);
            const openingTime = Math.max(
                reveal(elementOf(range.startContainer)),
                reveal(elementOf(range.endContainer))
            );
            if (openingTime) {
                pendingScroll.current = setTimeout(
                    () => scrollTo(range),
                    openingTime
                );
            } else {
                scrollTo(range);
            }
        },
        [placedRanges]
    );

    // Shows the new comment at once, while the list is fetched again.
    const commentPosted = useCallback(
        (comment) => {
            setDraft(null);
            setActiveCommentIds([comment.id]);
            // Among the others, now that it is one of them.
            setIsPanelCollapsed(false);
            document.getSelection()?.removeAllRanges();
            mutate(
                (current) =>
                    current && {
                        ...current,
                        comments: [comment, ...current.comments],
                    }
            );
        },
        [mutate]
    );

    // Takes the comment and its mark away at once, while the list is fetched
    // again.
    const commentDeleted = useCallback(
        (id) => {
            setActiveCommentIds((current) =>
                current.includes(id)
                    ? current.filter((activeId) => activeId !== id)
                    : current
            );
            mutate(
                (current) =>
                    current && {
                        ...current,
                        comments: current.comments.filter(
                            (comment) => comment.id !== id
                        ),
                    }
            );
        },
        [mutate]
    );

    const canComment = data?.can_comment ?? false;

    // Clicking a mark opens the panel on every comment marked there. A click
    // on a link or button in marked text is about that, and a click that
    // finishes a selection is about the selection.
    useEffect(() => {
        const root = commentableRef.current;
        if (!root) return;
        const openMarkedComments = (event) => {
            if (event.target.closest('a, button')) return;
            if (!document.getSelection()?.isCollapsed) return;
            const ids = commentsMarkedAt(
                placedRanges,
                event.clientX,
                event.clientY
            );
            if (!ids.length) return;
            clearTimeout(pendingScroll.current);
            setActiveCommentIds(ids);
            setIsPanelOpen(true);
            setIsPanelCollapsed(true);
        };
        root.addEventListener('click', openMarkedComments);
        return () => root.removeEventListener('click', openMarkedComments);
    }, [placedRanges]);

    // Marks every placed comment, with the active ones and the draft standing
    // out. The draft's text stays marked once the browser's selection is gone.
    // A selection stands out by itself.
    useEffect(() => {
        const activeRanges = [
            ...activeCommentIds.map((id) => placedRanges.get(id)),
            placements.draft,
        ].filter(Boolean);
        return paintMarks(
            Array.from(placedRanges.values()),
            activeRanges,
            activeRanges.length > 0 || hasSelection
        );
    }, [placedRanges, activeCommentIds, placements.draft, hasSelection]);

    const value = useMemo(
        () => ({
            pageId,
            readerName,
            comments,
            canComment,
            commentableRef,
            isPanelOpen,
            isPanelCollapsed,
            toggleCollapsed,
            togglePanel,
            closePanel,
            draft,
            startDraft,
            updateDraft,
            discardDraft,
            commentPosted,
            commentDeleted,
            activeCommentIds,
            placedRanges,
            draftRange: placements.draft,
            showComment,
            selectionChanged,
        }),
        [
            pageId,
            readerName,
            comments,
            canComment,
            isPanelOpen,
            isPanelCollapsed,
            toggleCollapsed,
            togglePanel,
            closePanel,
            draft,
            startDraft,
            updateDraft,
            discardDraft,
            commentPosted,
            commentDeleted,
            activeCommentIds,
            placedRanges,
            placements.draft,
            showComment,
        ]
    );

    return (
        <ReportCommentsContext.Provider value={value}>
            {children}
        </ReportCommentsContext.Provider>
    );
};

export const useReportComments = () => useContext(ReportCommentsContext);
